import React, { useState, useEffect } from "react";
import ClientAppShell from "../components/ClientAppShell";
import DashboardProjectContent from "../components/DashboardProjectContent";
import { usePageWalkthrough } from "../hooks/usePageWalkthrough";
import {
  getwalkthroughsteps,
  Addwalkthroughstepprogress,
} from "../services/dashboard";
import ProfileInfo from "./components/profile-info";
import {
  loadProjectCapabilities,
  loadProjectState,
  loadSelectedCapabilities,
  peekClientDataCache,
} from "../services/clientProjectData";
import { clientCacheKey } from "../utils/clientProjectDataCache";
import { useAuth } from "../auth/AuthContext";
import "../assets/css/dashboard.css";
import "../assets/css/dashboard-content.css";
import "../styles/client-dashboard.css";
import ContentLoader from "../components/ContentLoader";

const Dashboard = () => {
  const { user } = useAuth();
  const [selectedItem, setSelectedItem] = useState(null);
  const [projectState, setProjectState] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [capabilities, setCapabilities] = useState([]);
  const [selectedCapabilities, setSelectedCapabilities] = useState([]);
  const [selectedCounts, setSelectedCounts] = useState({});
  const [walkthroughSteps, setWalkthroughSteps] = useState([]);

  const { walkthrough } = usePageWalkthrough({
    userId,
    parentId: 1,
    targetMap: {
      "[data-nav=\"dashboard\"]": "[data-nav=\"dashboard\"]",
      "[data-action=\"capabilities\"]": "[data-element=\"capabilities\"]",
      "[data-element=\"profile\"]": "[data-action=\"user-profile\"]",
    },
  });

  const handleMenuSelection = (item) => {
    setSelectedItem(item);
    return item;
  };

  useEffect(() => {
    if (!userId) return;
    getwalkthroughsteps()
      .then((steps) => setWalkthroughSteps(steps))
      .catch(console.error);
  }, [userId]);

  useEffect(() => {
    const storedProjectId = localStorage.getItem("projectId");
    if (storedProjectId) {
      setProjectId(storedProjectId);
    }
  }, []);

  useEffect(() => {
    if (user?.userId) {
      setUserId(user.userId);
    }
  }, [user]);

  useEffect(() => {
    if (!projectId || !userId) return;

    const stateKey = clientCacheKey([
      "project_state",
      userId,
      String(parseInt(projectId, 10)),
    ]);
    const cachedStateEnvelope = peekClientDataCache<{ data?: { projectId: number; state: number }[] }>(
      stateKey
    );
    const cachedStateRow = cachedStateEnvelope?.data?.find(
      (item) => String(item.projectId) === String(projectId)
    );
    if (cachedStateRow) {
      setProjectState(cachedStateRow.state);
    }

    let cancelled = false;

    const fetchData = async () => {
      try {
        const [capabilitiesResponse, selectedData, fileData] = await Promise.all([
          loadProjectCapabilities(projectId),
          loadSelectedCapabilities({ userId, projectId }),
          loadProjectState({
            userId,
            projectId: [parseInt(projectId, 10)],
          }),
        ]);

        if (cancelled) return;

        setCapabilities(capabilitiesResponse.data);

        const preselected = selectedData.data.selectedCapabilities.flatMap((cap) =>
          cap.selectedSubCapabilities.map((subCap) => ({
            capabilityId: cap.capabilityId,
            subCapabilityId: subCap.subCapabilityId,
            state: subCap.state,
          }))
        );
        setSelectedCapabilities(preselected);

        const counts = {};
        preselected.forEach((item) => {
          counts[item.capabilityId] = (counts[item.capabilityId] || 0) + 1;
        });
        setSelectedCounts(counts);

        if (fileData.data) {
          const state = fileData.data.filter(
            (item) => item.projectId == localStorage.getItem("projectId")
          );
          if (state.length > 0) {
            setProjectState(state[0].state);
          }
        }
      } catch (error) {
        console.error("Error loading dashboard:", error);
      }
    };
    fetchData();

    return () => {
      cancelled = true;
    };
  }, [projectId, userId]);

  useEffect(() => {
    if (userId == null || projectState == null || !walkthroughSteps.length) return;

    const step = walkthroughSteps.find(
      (item) => item.stepOrder === projectState && item.parentId === null
    );
    if (!step) return;

    const pct = Math.round(
      (step.stepOrder /
        walkthroughSteps.filter((item) => item.parentId === null).length) *
        100
    );

    Addwalkthroughstepprogress({
      userId,
      stepId: step.id,
      isCompleted: true,
      isSkipped: false,
      completionPercentage: pct,
    }).catch(console.error);
  }, [projectState, userId, walkthroughSteps]);

  const selectedProject = (user?.projectDetails ?? []).find(
    (project) => String(project.projectId) === String(projectId)
  );

  return (
    <>
      {walkthrough}
      <ClientAppShell
        title="Dashboard"
        dataNav="dashboard"
        selectedItem={selectedItem}
        onMenuSelect={handleMenuSelection}
        profileSlot={
          <div id="profile-info" data-action="user-profile">
            <ProfileInfo />
          </div>
        }
      >
        <div className="dash-frame-wrapper">
          <div className="dash-frame-div">
            {projectState !== null && projectState !== undefined ? (
              <DashboardProjectContent
                state={projectState}
                userName={user?.userName}
                projectName={selectedProject?.projectName}
                companyName={selectedProject?.companyName}
                capabilities={capabilities}
                selectedCapabilities={selectedCapabilities}
                selectedCounts={selectedCounts}
              />
            ) : (
              <ContentLoader page label="Loading your project overview…" />
            )}
          </div>
        </div>
      </ClientAppShell>
    </>
  );
};

export default Dashboard;
